import store from 'app/store';
import {
  airHandlerTypeCodes,
  existingSystemComponentTypeProperties as componentTypeProperties
} from 'data/constants';
import {
  AirHandlerProductType,
  AttributeType,
  CondenserProductType,
  CondenserType,
  ConfigurationType,
  ExistingSystemComponentType,
  OversizeRangeType,
  SelectedDuctedSystemType,
  SelectedSystemType,
  SystemFiltersType,
  SystemOptionCondenserType,
  SystemOptionConfigurationType,
  SystemOptionType,
  SystemOptionZoneType,
  SystemScoreInlineType,
  ValidSystemType
} from 'data/types';
import { nanoid } from 'nanoid';
import axios from 'utils/api';
import { summedOperatingCostParts } from 'utils/aux-heat-cost-line';
import { averagePresentScores } from 'utils/average-present-scores';
import { requiredShareRatiosForConfiguration } from 'utils/required-share-ratio';
import { shareCoverageScoresForSystems } from 'utils/share-coverage';
import { combustionFurnaceMissingVentingMaterial } from 'utils/existing-equipment';
import { fileToBase64, isEmpty } from 'utils/helpers';
import {
  hasUsableLabelEvidence,
  LabelEvidence,
  parseLabelEvidence
} from 'utils/label-evidence';
import { sumCondenserAmperage } from 'utils/sanitize-system-option';
import { errorModal } from 'utils/ui';

export function truncateSelectedSystems<
  T extends (SelectedSystemType | SelectedDuctedSystemType)[]
>(systems: T): T {
  return systems.map((system) => {
    const configuration =
      'config' in system ? system.config : system.configuration;

    const truncatedCondensers = configuration.condensers.map((cond) => ({
      ...cond,
      selectedProduct: cond.selectedProduct
        ? { ...cond.selectedProduct, accessories: [] }
        : cond.selectedProduct,
      selectedAccessories: [],
      zones: cond.zones.map((zone) => ({
        ...zone,
        selectedProduct: zone.selectedProduct
          ? { ...zone.selectedProduct, accessories: [] }
          : zone.selectedProduct
      }))
    }));

    const configKey = 'config' in system ? 'config' : 'configuration';

    return {
      ...system,
      [configKey]: { ...configuration, condensers: truncatedCondensers },
      selectedAccessories: [],
      quotableAccessories: [],
      tax: undefined,
      shipping: undefined,
      rebates: undefined,
      totalRebates: undefined
    };
  }) as unknown as T;
}

export function getOutdoorVisibility(
  dimensions:
    | AirHandlerProductType['dimensions']
    | CondenserProductType['dimensions']
) {
  const { depth, height, width } = dimensions;
  const totalSize = depth * height * width;

  if (totalSize <= 12498.42) {
    return 'Least Visible';
  }
  if (totalSize <= 20167.73) {
    return 'Less Visible';
  }
  return 'Most Visible';
}

export function getMostVisibleOutdoorVisibility(
  visibility: ('Least Visible' | 'Less Visible' | 'Most Visible')[]
) {
  let mostVisibleVisibility: 'Least Visible' | 'Less Visible' | 'Most Visible' =
    'Least Visible';

  if (visibility.includes('Less Visible')) {
    mostVisibleVisibility = 'Less Visible';
  }
  if (visibility.includes('Most Visible')) {
    mostVisibleVisibility = 'Most Visible';
  }
  return mostVisibleVisibility;
}

export function getValidManufacturers(config: ConfigurationType) {
  let validManufacturers: string[] = [];
  if (config.condensers.length >= 1) {
    config.condensers.forEach((cond) => {
      const newManufacturers = cond.validSystems
        .filter((system) =>
          cond.zones.every((z) => {
            const zoneIds = [
              system.airHandler1Unique,
              system.airHandler2Unique,
              system.airHandler3Unique,
              system.airHandler4Unique,
              system.airHandler5Unique,
              system.airHandler6Unique,
              system.airHandler7Unique,
              system.airHandler8Unique,
              system.airHandler9Unique,
              system.airHandler10Unique
            ];
            const airHandlerTypes = [
              system.airHandler1Type,
              system.airHandler2Type,
              system.airHandler3Type,
              system.airHandler4Type,
              system.airHandler5Type,
              system.airHandler6Type,
              system.airHandler7Type,
              system.airHandler8Type,
              system.airHandler9Type,
              system.airHandler10Type
            ];
            return (
              !z.selectedProduct ||
              zoneIds.some(
                (zoneId, index) =>
                  z.selectedProduct &&
                  z.uniqueId === zoneId &&
                  airHandlerTypeCodes[z.selectedProduct.type] ===
                    airHandlerTypes[index]
              )
            );
          })
        )
        .map((system) => system.manufacturer);

      if (validManufacturers.length === 0) {
        validManufacturers = newManufacturers;
      } else {
        validManufacturers = newManufacturers.filter((manufacturer) =>
          validManufacturers.includes(manufacturer)
        );
      }
    });
  }
  return Array.from(new Set(validManufacturers));
}

export function getCondenserOptions(
  condenser: CondenserType,
  condenserProducts: CondenserProductType[]
) {
  const validProductIds = [
    ...new Set(
      condenser.validSystems
        .filter((s) => {
          let valid = false;
          const zoneIds = [
            s.airHandler1Unique,
            s.airHandler2Unique,
            s.airHandler3Unique,
            s.airHandler4Unique,
            s.airHandler5Unique,
            s.airHandler6Unique,
            s.airHandler7Unique,
            s.airHandler8Unique,
            s.airHandler9Unique,
            s.airHandler10Unique
          ];
          const airHandlerTypes = [
            s.airHandler1Type,
            s.airHandler2Type,
            s.airHandler3Type,
            s.airHandler4Type,
            s.airHandler5Type,
            s.airHandler6Type,
            s.airHandler7Type,
            s.airHandler8Type,
            s.airHandler9Type,
            s.airHandler10Type
          ];
          if (
            condenser.zones.every((zone) =>
              zoneIds.some(
                (zoneId, index) =>
                  zone.uniqueId === zoneId &&
                  zone.selectedProduct &&
                  airHandlerTypeCodes[zone.selectedProduct.type] ===
                    airHandlerTypes[index]
              )
            )
          ) {
            valid = true;
          }
          return valid;
        })
        .map((s) => s.condenserProductId)
    )
  ];
  const validProducts = validProductIds
    .map((id) => {
      const product = condenserProducts.find((p) => p.productId === id);
      if (!product) {
        console.error(`Product with id ${id} not found`);
      }
      return product;
    })
    .filter((p) => p) as CondenserProductType[];
  return validProducts;
}

export function buildCondenserViewFromFilteredSystem(
  filteredSystem: ValidSystemType,
  sourceCondenser: CondenserType,
  condenserProducts: CondenserProductType[],
  airHandlerProducts: AirHandlerProductType[]
): SystemOptionCondenserType | null {
  const condenserProduct = condenserProducts.find(
    (p) => p.productId === filteredSystem.condenserProductId
  );
  if (!condenserProduct) return null;

  const outdoorVisibility = getOutdoorVisibility(condenserProduct.dimensions);

  const zones: SystemOptionZoneType[] = sourceCondenser.zones
    .map((zone) => {
      let productId: number | null = null;
      for (let n = 1; n <= 10; n += 1) {
        const uniqueKey = `airHandler${n}Unique` as keyof ValidSystemType;
        const productKey = `airHandler${n}ProductId` as keyof ValidSystemType;
        if (filteredSystem[uniqueKey] === zone.uniqueId) {
          productId = filteredSystem[productKey] as number | null;
          break;
        }
      }
      if (productId == null) return null;

      const airHandlerProduct = airHandlerProducts.find(
        (p) => p.productId === productId
      );
      if (!airHandlerProduct) return null;

      return {
        uniqueId: zone.uniqueId,
        name: zone.name,
        type: zone.type ?? 'ductless',
        zoneItems: zone.zoneItems ?? [],
        selectedProduct: airHandlerProduct,
        distanceFromCondenser: zone.distanceFromCondenser,
        selectedLineSet: zone.selectedLineSet,
        selectedDuctingKit: zone.selectedDuctingKit,
        addWire: zone.addWire
      } as SystemOptionZoneType;
    })
    .filter((z): z is SystemOptionZoneType => z !== null);

  return {
    uniqueId: sourceCondenser.uniqueId,
    name: sourceCondenser.name,
    type: sourceCondenser.type ?? 'ductless',
    selectedProduct: { ...condenserProduct, outdoorVisibility },
    zones,
    selectedAccessories: sourceCondenser.selectedAccessories ?? [],
    accessoriesCompleted: sourceCondenser.accessoriesCompleted ?? [],
    accessoriesNotNeeded: sourceCondenser.accessoriesNotNeeded ?? [],
    noAccessoriesNeeded: sourceCondenser.noAccessoriesNeeded,
    heightOffGround: sourceCondenser.heightOffGround
  };
}

export function createSystemOption(
  systems: ValidSystemType[],
  config: ConfigurationType,
  name = 'Custom System',
  tags: string[] = [],
  totalRebates: number | undefined = undefined,
  score: SystemScoreInlineType | null | undefined = undefined
): SystemOptionType {
  const state = store.getState();
  const { condenserProducts, airHandlerProducts } = state.data;
  // Partial stores (tests, early boot) fall back to the slice defaults.
  const requiredRatios = requiredShareRatiosForConfiguration(
    config,
    state.structure?.structure ?? [],
    state.data.supplementalHeatingWeight ?? 0.5,
    state.data.supplementalCoolingWeight ?? 0.5
  );
  const equippedCondensers = config.condensers
    .map((cond) => {
      const matchingSystem = systems.find(
        (s) => s.condenserUnique === cond.uniqueId
      );
      if (!matchingSystem) return null;
      const built = buildCondenserViewFromFilteredSystem(
        matchingSystem,
        cond,
        condenserProducts,
        airHandlerProducts
      );
      if (!built || built.zones.length === 0) return null;
      return built;
    })
    .filter((cond): cond is SystemOptionCondenserType => cond !== null);

  const newConfiguration: SystemOptionConfigurationType = {
    uniqueId: config.uniqueId,
    name: config.name,
    condensers: equippedCondensers
  };
  const productAestheticsRanks: number[] = [];
  const productInstallationDifficultyRanks: number[] = [];
  systems.forEach((system) => {
    [
      system.airHandler1ProductId,
      system.airHandler2ProductId,
      system.airHandler3ProductId,
      system.airHandler4ProductId,
      system.airHandler5ProductId,
      system.airHandler6ProductId,
      system.airHandler7ProductId,
      system.airHandler8ProductId,
      system.airHandler9ProductId,
      system.airHandler10ProductId
    ]
      .filter((id) => id !== null)
      .forEach((productId) => {
        const product = airHandlerProducts.find(
          (p) => p.productId === productId
        );
        if (product) {
          productAestheticsRanks.push(product.aestheticsRank);
          productInstallationDifficultyRanks.push(
            product.installComplexityRank
          );
        }
      });
  });
  const manufacturers = Array.from(
    new Set(systems.map((system) => system.manufacturer))
  );
  const newTags: string[] = [...tags];
  const condensers = systems
    .map((s) =>
      condenserProducts.find((p) => p.productId === s.condenserProductId)
    )
    .filter((p) => p) as CondenserProductType[];
  if (manufacturers.length > 1) {
    newTags.push('Mixed Manufacturers');
  }
  const outdoorVisibilities = newConfiguration.condensers
    .map((cond) => {
      const { selectedProduct } = cond;
      return selectedProduct
        ? getOutdoorVisibility(selectedProduct.dimensions)
        : null;
    })
    .filter((v) => v) as ('Least Visible' | 'Less Visible' | 'Most Visible')[];
  const outdoorVisibility =
    getMostVisibleOutdoorVisibility(outdoorVisibilities);

  const seer2Values = systems
    .map((s) => s.seer2 ?? s.seer ?? null)
    .filter((v): v is number => typeof v === 'number');
  const minSeer2 = seer2Values.length ? Math.min(...seer2Values) : undefined;
  const maxSeer2 = seer2Values.length ? Math.max(...seer2Values) : undefined;

  return {
    id: `${config.uniqueId}-${systems.map((s) => s.id).join('-')}`,
    uniqueId: nanoid(5),
    name,
    price: systems.reduce((total, system) => system.baseRetailPrice + total, 0),
    numberOfCondensers: equippedCondensers.length,
    numberOfZones: equippedCondensers.reduce(
      (total, condenser) => total + condenser.zones.length,
      0
    ),
    btu: condensers.reduce((total, product) => product.coolingBTU + total, 0),
    // A condenser without heating performance data has no score; it must not
    // pull the average toward zero. The schema keeps this numeric, so a
    // system with no scores at all reads 0.
    heatingScore:
      averagePresentScores(systems.map((system) => system.heatingScore)) ?? 0,
    coolingScore: averagePresentScores(
      systems.map((system) => system.coolingScore)
    ) as SystemOptionType['coolingScore'],
    ...(requiredRatios as Partial<SystemOptionType>),
    ...(shareCoverageScoresForSystems(systems) as Partial<SystemOptionType>),
    condenserCoverage: systems.map((system) => {
      const condenser = config.condensers.find(
        (cond) => cond.uniqueId === system.condenserUnique
      );

      return {
        uniqueId: condenser?.uniqueId ?? nanoid(5),
        name: condenser?.name ?? 'Unknown Condenser',
        hoursOfInsufficientCooling: system.hoursOfInsufficientCooling,
        percentageOfTimeOfInsufficientCooling:
          system.percentageOfTimeOfInsufficientCooling,
        avgPartialCoolingCoveragePercent:
          system.avgPartialCoolingCoveragePercent,
        hoursOfInsufficientHeating: system.hoursOfInsufficientHeating,
        percentageOfTimeOfInsufficientHeating:
          system.percentageOfTimeOfInsufficientHeating,
        avgPartialHeatingCoveragePercent:
          system.avgPartialHeatingCoveragePercent,
        heatingScore: system.heatingScore ?? null,
        coolingScore: system.coolingScore ?? null,
        maxTempAtCoolingScore: system.maxTempAtCoolingScore ?? null,
        minTempAtHeatingScore: system.minTempAtHeatingScore ?? null
      };
    }),
    configuration: newConfiguration,
    inStock: systems.every((system) => system.inStock),
    restockDate: null,
    totalAnnualCost: systems.some((system) => system.totalAnnualCost == null)
      ? null
      : systems.reduce(
          (total, system) => total + (system.totalAnnualCost ?? 0),
          0
        ),
    ...(summedOperatingCostParts(systems) as Partial<SystemOptionType>),
    averageSeer: Math.round(
      equippedCondensers.reduce(
        (acc, cond) => acc + (cond.selectedProduct?.seer ?? 0),
        0
      ) / (equippedCondensers.length || 1)
    ),
    minSeer2,
    maxSeer2,
    averageAestheticsRank:
      productAestheticsRanks.length === 0
        ? 0
        : productAestheticsRanks.reduce((acc, rank) => acc + rank, 0) /
          productAestheticsRanks.length,
    installationDifficultyScore: productInstallationDifficultyRanks.reduce(
      (acc, rank) => acc + rank,
      0
    ),
    manufacturers,
    onSale:
      newConfiguration.condensers.some(
        (cond) => cond.selectedProduct?.isOnSale
      ) ||
      newConfiguration.condensers.some((cond) =>
        cond.zones.some((zone) => zone.selectedProduct?.isOnSale)
      ),
    isMatchingAirHandlers: systems.every(
      (s) => s.isMatchingAirHandlers === true
    ),
    tags: newTags,
    totalRebates,
    score: score ?? null,
    amperage: sumCondenserAmperage(equippedCondensers),
    outdoorVisibility
  };
}

export type CustomSystemEntryInput = {
  condenserUniqueId: string;
  condenserProductId: number;
  airHandlers: Array<{ zoneUniqueId: string; airHandlerProductId: number }>;
};

export function buildCustomSystemEntries(
  systemsForNewOption: ValidSystemType[],
  config: ConfigurationType
): CustomSystemEntryInput[] {
  return config.condensers
    .filter((c) => c.position !== undefined)
    .map((cond) => {
      const system = systemsForNewOption.find(
        (s) => s.condenserUnique === cond.uniqueId
      );
      if (!system) return null;
      const airHandlers: CustomSystemEntryInput['airHandlers'] = [];
      for (let i = 1; i <= 10; i += 1) {
        const slotUnique = (system as unknown as Record<string, unknown>)[
          `airHandler${i}Unique`
        ] as string | null | undefined;
        const slotProductId = (system as unknown as Record<string, unknown>)[
          `airHandler${i}ProductId`
        ] as number | null | undefined;
        if (slotUnique && typeof slotProductId === 'number') {
          airHandlers.push({
            zoneUniqueId: slotUnique,
            airHandlerProductId: slotProductId
          });
        }
      }
      return {
        condenserUniqueId: cond.uniqueId,
        condenserProductId: system.condenserProductId,
        airHandlers
      };
    })
    .filter((e): e is CustomSystemEntryInput => e !== null);
}

export function filterSystems(
  systems: ValidSystemType[],
  filters: SystemFiltersType,
  customerValues: AttributeType[],
  condenser: CondenserType
) {
  const isCondenserOnSale = condenser?.selectedProduct?.isOnSale ?? false;
  const isAnyZoneOnSale = condenser.zones?.some(
    (zone) => zone?.selectedProduct?.isOnSale
  );

  return [...systems]
    .filter((system) => !filters.inStock || system.inStock)
    .filter(
      (system) =>
        !filters.belowBudget ||
        system.baseRetailPrice <=
          (customerValues.find((cv) => cv.code === 'max_budget')?.value ??
            999999)
    )
    .filter(
      (system) =>
        !filters.selectedAirHandlers ||
        condenser.zones.every(
          (zone) =>
            zone.selectedProduct &&
            ((zone.uniqueId === system.airHandler1Unique &&
              system.airHandler1Type ===
                airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler2Unique &&
                system.airHandler2Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler3Unique &&
                system.airHandler3Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler4Unique &&
                system.airHandler4Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler5Unique &&
                system.airHandler5Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler6Unique &&
                system.airHandler6Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler7Unique &&
                system.airHandler7Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler8Unique &&
                system.airHandler8Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler9Unique &&
                system.airHandler9Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]) ||
              (zone.uniqueId === system.airHandler10Unique &&
                system.airHandler10Type ===
                  airHandlerTypeCodes[zone.selectedProduct.type]))
        )
    )
    .filter(
      (system) =>
        !filters.isOnSale ||
        isAnyZoneOnSale ||
        isCondenserOnSale ||
        system.discountAmount > 0
    );
  // TODO: improve onSale filter to check if any individual product is on sale as discountAmount does not always reflect this
}

export function mergeWithCustomOptions(
  currentOptions: Record<string, SystemOptionType[]>,
  newOptions: Record<string, SystemOptionType[]>
): Record<string, SystemOptionType[]> {
  const result = { ...newOptions };
  Object.entries(currentOptions).forEach(([configId, options]) => {
    const custom = options.filter((o) => o.tags.includes('Custom'));
    if (custom.length > 0) {
      result[configId] = [...custom, ...(result[configId] ?? [])];
    }
  });
  return result;
}

export function sortSystemOptions(
  systemOptions: SystemOptionType[],
  customerValues: AttributeType[]
) {
  const customerCaresAboutAesthetics =
    customerValues.find((cv) => cv.code === 'aesthetic_preference')?.value ===
    'Hidden';
  const uniqueConfigIds = Array.from(
    new Set(systemOptions.map((o) => o.configuration.uniqueId))
  );
  const uniqueConfigs = uniqueConfigIds
    .map(
      (id) =>
        systemOptions.find((o) => o.configuration.uniqueId === id)
          ?.configuration
    )
    .filter((c) => c) as SystemOptionConfigurationType[];
  const systemOptionsMatrix: SystemOptionType[][] = [];
  const newSystemOptions: SystemOptionType[] = [];

  uniqueConfigs.forEach((config) => {
    const options = systemOptions.filter(
      (o) => o.configuration.uniqueId === config.uniqueId
    );
    systemOptionsMatrix.push(options);
  });

  const addSystemOption = (systemOrTag: SystemOptionType | string) => {
    const system =
      typeof systemOrTag === 'string'
        ? systemOptionsMatrix.flat().find((o) => o.tags.includes(systemOrTag))
        : systemOrTag;
    if (
      system &&
      !newSystemOptions.map((o) => o.uniqueId).includes(system.uniqueId)
    ) {
      newSystemOptions.push(system);
    }
  };

  addSystemOption('Recommended');
  addSystemOption('Preferred Air Handlers');
  if (customerCaresAboutAesthetics) {
    addSystemOption('Most Aesthetic');
  }
  addSystemOption('Best Coverage');

  // if third option not on sale, find next highest ranking on sale system and insert it
  newSystemOptions.push(
    ...systemOptions.filter(
      (o) => !newSystemOptions.some((no) => no.uniqueId === o.uniqueId)
    )
  );
  if (newSystemOptions[2]?.onSale !== true) {
    const bestOnSaleSystem = newSystemOptions.find((no) => no.onSale === true);
    if (bestOnSaleSystem) {
      newSystemOptions.splice(2, 0, bestOnSaleSystem);
    }
  }

  // Remove any duplicates
  const uniqueNewSystemOptions = newSystemOptions.reduce((acc, curr) => {
    if (!acc.some((item) => item.uniqueId === curr.uniqueId)) {
      acc.push(curr);
    }
    return acc;
  }, [] as SystemOptionType[]);

  return uniqueNewSystemOptions;
}

export type ModelNumberAnalysis = {
  modelNumber: string | null;
  evidence: LabelEvidence | null;
};

export async function determineModelNumberFromImage(
  image: File
): Promise<ModelNumberAnalysis> {
  const base64Image = await fileToBase64(image);

  if (!base64Image) {
    errorModal('No valid images could be processed.');
    console.error('No valid images could be processed.');
    return { modelNumber: null, evidence: null };
  }

  const determineModelNumber = await axios.post(
    'ai/get-model-number-from-image',
    { labelImage: base64Image }
  );

  return {
    modelNumber: determineModelNumber.data.modelNumber ?? null,
    evidence: parseLabelEvidence(determineModelNumber.data)
  };
}

export async function determineModelNumberFromAttachment(
  attachmentId: number
): Promise<ModelNumberAnalysis> {
  const { structureUniqueId } = store.getState().structure;

  if (!structureUniqueId) {
    return { modelNumber: null, evidence: null };
  }

  const determineModelNumber = await axios.post(
    'ai/get-model-number-from-image',
    { attachmentId, structureUniqueId }
  );

  return {
    modelNumber: determineModelNumber.data.modelNumber ?? null,
    evidence: parseLabelEvidence(determineModelNumber.data)
  };
}

export async function determineAmperageFromAttachment(
  attachmentId: number
): Promise<number | null> {
  const { structureUniqueId } = store.getState().structure;

  if (!structureUniqueId) {
    return null;
  }

  const determineAmperage = await axios.post('ai/get-amperage-from-image', {
    attachmentId,
    structureUniqueId
  });

  const { amperage } = determineAmperage.data;

  return typeof amperage === 'number' ? amperage : null;
}

export async function determineSystemComponentFromModelNumber(
  modelNumber: string,
  manufacturer?: string,
  labelEvidence?: LabelEvidence | null
): Promise<Partial<ExistingSystemComponentType> | null> {
  if (!modelNumber) {
    errorModal('No valid model number could be processed.');
    console.error('No valid model number could be processed.');
    return null;
  }

  // Manufacturer and label evidence are optional context for the AI lookup:
  // omit each entirely unless it carries content, so the request (and the
  // prompt it feeds) stays identical to the context-less behavior.
  const trimmedManufacturer = manufacturer?.trim();
  const determineComponent = await axios.post(
    'ai/get-specs-from-model-number',
    {
      modelNumber,
      ...(trimmedManufacturer ? { manufacturer: trimmedManufacturer } : {}),
      ...(hasUsableLabelEvidence(labelEvidence) ? { labelEvidence } : {})
    }
  );

  return determineComponent.data;
}

// Evidence-only lookup: no model number was readable, so the analysis rests
// entirely on the transcribed label facts. A separate function (rather than
// a nullable first arg above) keeps the existing model-number call sites
// untouched.
export async function determineSystemComponentFromLabelEvidence(
  labelEvidence: LabelEvidence,
  manufacturer?: string
): Promise<Partial<ExistingSystemComponentType> | null> {
  if (!hasUsableLabelEvidence(labelEvidence)) {
    return null;
  }

  const trimmedManufacturer = manufacturer?.trim();
  const determineComponent = await axios.post(
    'ai/get-specs-from-model-number',
    {
      labelEvidence,
      ...(trimmedManufacturer ? { manufacturer: trimmedManufacturer } : {})
    }
  );

  // Without a model number in the request there is nothing a returned
  // modelNumber could be but fabricated — never let one through.
  const { modelNumber: fabricated, ...componentProps } =
    determineComponent.data ?? {};
  return componentProps;
}

export function systemComponentIsValid(
  systemComponent: ExistingSystemComponentType
) {
  if (!systemComponent.type) return false;
  const propertiesConfig = componentTypeProperties[systemComponent.type];
  if (!propertiesConfig) return false;
  const staticValid = Object.entries(propertiesConfig).every(
    ([propertyKey, propertyConfig]) =>
      !propertyConfig.required ||
      !isEmpty(
        systemComponent[propertyKey as keyof ExistingSystemComponentType]
      )
  );
  // Conditional rules (AHA-6796): keep validity aligned with
  // componentHasMissingRequiredDetails — a combustion furnace without its
  // venting material is incomplete here too (gray/red borders, equipment
  // images).
  return (
    staticValid && !combustionFurnaceMissingVentingMaterial(systemComponent)
  );
}

export function calculateOperatingCost(
  coolingCapacity: number,
  seer: number,
  costPerKWH: number
) {
  return Math.round(
    ((24 * 1200) / 12) * (coolingCapacity / seer) * (costPerKWH / 1000)
  );
}

export function getOversizeAmount(
  oversizeRanges: OversizeRangeType[],
  BTU: number
) {
  const range = oversizeRanges.find(
    (r) => (r.minBTU ?? 0) <= BTU && (r.maxBTU ?? 9999999999) >= BTU
  );
  return range ? range.oversizeBTUAmount : 0;
}

export function getZoneNumberById(system: ValidSystemType, zoneId: string) {
  if (system.airHandler1Unique === zoneId) return 1;
  if (system.airHandler2Unique === zoneId) return 2;
  if (system.airHandler3Unique === zoneId) return 3;
  if (system.airHandler4Unique === zoneId) return 4;
  if (system.airHandler5Unique === zoneId) return 5;
  if (system.airHandler6Unique === zoneId) return 6;
  if (system.airHandler7Unique === zoneId) return 7;
  if (system.airHandler8Unique === zoneId) return 8;
  if (system.airHandler9Unique === zoneId) return 9;
  if (system.airHandler10Unique === zoneId) return 10;
  return 0;
}

export function removeNonComparisonSystemTags<
  T extends SelectedDuctedSystemType | SelectedSystemType
>(systems: T[]): T[] {
  return systems.map((system) => ({
    ...system,
    tags: system.tags?.filter((tag) =>
      ['sale', 'clearance', 'in stock', 'out of stock'].includes(
        tag.toLowerCase()
      )
    )
  }));
}

export function reassignDuctedSystemTags(
  systems: SelectedDuctedSystemType[]
): SelectedDuctedSystemType[] {
  if (systems.length <= 1) {
    return removeNonComparisonSystemTags(systems);
  }

  const updatedSystems = systems.map((system) => ({
    ...system,
    tags: [] as string[]
  }));
  const cheapestSystem = systems.reduce(
    (min, current) => (current.price < min.price ? current : min),
    systems[0]
  );
  const cheapestIndex = updatedSystems.findIndex(
    (s) => s.id === cheapestSystem.id
  );
  const bestHeatingSystem = systems.reduce(
    (best, current) =>
      (current.heatingScore || 0) > (best.heatingScore || 0) ? current : best,
    systems[0]
  );
  const bestHeatingIndex = updatedSystems.findIndex(
    (s) => s.id === bestHeatingSystem.id
  );
  const bestCoolingSystem = systems.reduce(
    (best, current) =>
      (current.coolingScore || 0) > (best.coolingScore || 0) ? current : best,
    systems[0]
  );
  const bestCoolingIndex = updatedSystems.findIndex(
    (s) => s.id === bestCoolingSystem.id
  );
  const mostEfficientSystem = systems.reduce(
    (best, current) =>
      (current.seer || 0) > (best.seer || 0) ? current : best,
    systems[0]
  );
  const mostEfficientIndex = updatedSystems.findIndex(
    (s) => s.id === mostEfficientSystem.id
  );

  if (cheapestIndex !== -1) {
    updatedSystems[cheapestIndex].tags.push('Cheapest');
  }

  if (bestHeatingIndex !== -1) {
    updatedSystems[bestHeatingIndex].tags.push('Full Heating');
  }

  if (bestCoolingIndex !== -1) {
    updatedSystems[bestCoolingIndex].tags.push('Full Cooling');
  }

  if (mostEfficientIndex !== -1) {
    updatedSystems[mostEfficientIndex].tags.push('Efficient');
  }

  return updatedSystems;
}

export function reassignDuctlessSystemTags(
  systems: SelectedSystemType[]
): SelectedSystemType[] {
  if (systems.length <= 1) {
    return removeNonComparisonSystemTags(systems);
  }

  const updatedSystems = systems.map((system) => ({
    ...system,
    tags: [] as typeof system.tags
  }));
  const cheapestSystem = systems.reduce(
    (min, current) => (current.price < min.price ? current : min),
    systems[0]
  );
  const cheapestIndex = updatedSystems.findIndex(
    (s) => s.uniqueId === cheapestSystem.uniqueId
  );
  const bestCoverageSystem = systems.reduce((best, current) => {
    const currentCoverage =
      (current.heatingScore || 0) + (current.coolingScore || 0);
    const bestCoverage = (best.heatingScore || 0) + (best.coolingScore || 0);
    return currentCoverage > bestCoverage ? current : best;
  }, systems[0]);
  const bestCoverageIndex = updatedSystems.findIndex(
    (s) => s.uniqueId === bestCoverageSystem.uniqueId
  );
  const mostEfficientSystem = systems.reduce(
    (best, current) =>
      (current.averageSeer || 0) > (best.averageSeer || 0) ? current : best,
    systems[0]
  );
  const mostEfficientIndex = updatedSystems.findIndex(
    (s) => s.uniqueId === mostEfficientSystem.uniqueId
  );
  const mostAestheticSystem = systems.reduce(
    (best, current) =>
      (current.averageAestheticsRank || 0) > (best.averageAestheticsRank || 0)
        ? current
        : best,
    systems[0]
  );
  const mostAestheticIndex = updatedSystems.findIndex(
    (s) => s.uniqueId === mostAestheticSystem.uniqueId
  );
  const easiestInstallationSystem = systems.reduce(
    (best, current) =>
      (current.installationDifficultyScore || 0) <
      (best.installationDifficultyScore || 0)
        ? current
        : best,
    systems[0]
  );
  const easiestInstallationIndex = updatedSystems.findIndex(
    (s) => s.uniqueId === easiestInstallationSystem.uniqueId
  );

  if (cheapestIndex !== -1) {
    updatedSystems[cheapestIndex].tags.push('Cheapest');
  }

  if (bestCoverageIndex !== -1) {
    updatedSystems[bestCoverageIndex].tags.push('Best Coverage');
  }

  if (mostEfficientIndex !== -1) {
    updatedSystems[mostEfficientIndex].tags.push('Most Efficient');
  }

  if (mostAestheticIndex !== -1) {
    updatedSystems[mostAestheticIndex].tags.push('Most Aesthetic');
  }

  if (easiestInstallationIndex !== -1) {
    updatedSystems[easiestInstallationIndex].tags.push('Easiest Installation');
  }

  return updatedSystems;
}

export type ExtractedProduct = {
  productTypeCode: 'condenser' | 'air_handler' | 'accessory';
  productId: number;
  quantity: number;
  configurationItemUniqueId?: string;
};

export function extractProductsFromSystem(
  system: SystemOptionType
): ExtractedProduct[] {
  const products: ExtractedProduct[] = [];

  system.configuration.condensers.forEach((condenser) => {
    if (condenser.selectedProduct?.productId) {
      products.push({
        productTypeCode: 'condenser',
        productId: condenser.selectedProduct.productId,
        quantity: 1,
        configurationItemUniqueId: condenser.uniqueId
      });
    }

    condenser.zones.forEach((zone) => {
      if (zone.selectedProduct?.productId) {
        products.push({
          productTypeCode: 'air_handler',
          productId: zone.selectedProduct.productId,
          quantity: 1,
          configurationItemUniqueId: zone.uniqueId
        });
      }
    });
  });

  const aggregatedAccessories = system.configuration.condensers
    .flatMap((cond) => cond.selectedAccessories ?? [])
    .reduce<Array<{ productId: number; quantity: number }>>((acc, item) => {
      const existing = acc.find((a) => a.productId === item.productId);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        acc.push({ productId: item.productId, quantity: item.quantity });
      }
      return acc;
    }, []);

  aggregatedAccessories.forEach((accessory) => {
    products.push({
      productTypeCode: 'accessory',
      productId: accessory.productId,
      quantity: accessory.quantity
    });
  });

  return products;
}
